import { motion } from 'framer-motion';
import { ContactInfo } from '../data/portfolioData';

interface ContactPanelProps {
  contact: ContactInfo;
  description?: string;
  color: string;
}

const GithubIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M22 4L12 13L2 4" />
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
  </svg>
);

export default function ContactPanel({ contact, description, color }: ContactPanelProps) {
  const links = [
    { icon: <EmailIcon />, label: 'Email', href: `mailto:${contact.email}`, value: contact.email },
    { icon: <GithubIcon />, label: 'GitHub', href: contact.github, value: 'GitHub Profile' },
    { icon: <LinkedInIcon />, label: 'LinkedIn', href: contact.linkedin, value: 'LinkedIn Profile' },
  ];

  return (
    <div>
      {description && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-gray-400 text-sm leading-relaxed mb-6"
        >
          {description}
        </motion.p>
      )}

      <div className="space-y-3">
        {links.map((link, i) => (
          <motion.a
            key={link.label}
            href={link.href}
            target={link.label !== 'Email' ? '_blank' : undefined}
            rel="noopener noreferrer"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.1 }}
            className="flex items-center gap-4 p-4 rounded-xl border
                       backdrop-blur-md transition-all duration-300
                       hover:scale-[1.02] group"
            style={{
              background: `linear-gradient(135deg, ${color}08, ${color}03)`,
              borderColor: `${color}25`,
              color: color,
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = `${color}50`;
              (e.currentTarget as HTMLElement).style.boxShadow = `0 0 20px ${color}20`;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor = `${color}25`;
              (e.currentTarget as HTMLElement).style.boxShadow = 'none';
            }}
          >
            <div className="opacity-70 group-hover:opacity-100 transition-opacity">
              {link.icon}
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">{link.label}</div>
              <div className="text-sm text-gray-300">{link.value}</div>
            </div>
          </motion.a>
        ))}

        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="flex items-center gap-4 p-4 rounded-xl border w-full
                     backdrop-blur-md transition-all duration-300
                     hover:scale-[1.02] group text-left"
          style={{
            background: `linear-gradient(135deg, ${color}15, ${color}08)`,
            borderColor: `${color}40`,
            color: color,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.boxShadow = `0 0 25px ${color}30`;
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.boxShadow = 'none';
          }}
        >
          <DownloadIcon />
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">Resume</div>
            <div className="text-sm text-gray-300">Download CV</div>
          </div>
        </motion.button>
      </div>
    </div>
  );
}
